import { session } from "../auth/session.js";
import { injectTheme } from "./theme.js";
import { showDiscordBadge } from "./discordBadge.js";
import { mountGhostLogo } from "./ghostLogo.js";

const PASSWORD_MIN = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STYLE_ID = "ghostland-auth-style";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-auth-overlay {
  position: fixed; inset: 0; z-index: 10;
  display: flex; align-items: center; justify-content: center;
  background: #000; color: #f4f4f4;
}
/* this screen is intentionally PIXEL-styled (overrides the global sans face) */
#gl-auth-overlay, #gl-auth-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }

/* the card: a chunky pixel frame — double inset rule + hard pixel drop-shadow, no radius */
#gl-auth-card {
  position: relative;
  width: 340px; padding: 38px 30px 30px;
  background: #0b0b0b;
  border: 3px solid #f4f4f4; border-radius: 0;
  box-shadow:
    inset 0 0 0 3px #000,
    inset 0 0 0 6px #2c2c2c,
    0 0 0 3px #000,
    10px 10px 0 0 rgba(0,0,0,.6);
}
/* pixel corner decorations — white rivets pinned at each outer corner */
.gl-auth-corner { position: absolute; width: 10px; height: 10px; background: #f4f4f4; box-shadow: 0 0 0 3px #000; }
.gl-auth-corner.tl { top: -7px; left: -7px; }
.gl-auth-corner.tr { top: -7px; right: -7px; }
.gl-auth-corner.bl { bottom: -7px; left: -7px; }
.gl-auth-corner.br { bottom: -7px; right: -7px; }

#gl-auth-card .sub {
  margin: 0 0 26px; text-align: center;
  font-size: 17px; line-height: 1.45; color: #f4f4f4; letter-spacing: 0.04em;
  text-shadow: 0 2px 0 #000;
}
#gl-auth-card label {
  display: block; font-size: 11px; color: #8a8a8a; letter-spacing: 0.18em;
  text-transform: uppercase; margin: 16px 0 6px;
}
#gl-auth-card input {
  width: 100%; box-sizing: border-box; padding: 12px 12px;
  background: #050505; border: 2px solid #2e2e2e; border-radius: 0;
  color: #f4f4f4; font-size: 14px; letter-spacing: 0.04em;
}
#gl-auth-card input:focus { outline: none; border-color: #f4f4f4; box-shadow: inset 0 0 0 1px #f4f4f4; }

/* pixel-design buttons: chunky, uppercase, hard shadow, tactile press */
#gl-auth-card button.primary {
  width: 100%; margin-top: 26px; padding: 14px 12px; cursor: pointer;
  background: #f4f4f4; border: 3px solid #fff; border-radius: 0;
  color: #000; font-size: 15px; letter-spacing: 0.16em; text-transform: uppercase;
  box-shadow: 4px 4px 0 0 #000;
}
#gl-auth-card button.primary:hover:not(:disabled) { background: #fff; }
#gl-auth-card button.primary:active:not(:disabled) { transform: translate(2px, 2px); box-shadow: 2px 2px 0 0 #000; }
#gl-auth-card button.primary:disabled { opacity: 0.4; cursor: default; }
#gl-auth-toggle { margin-top: 20px; text-align: center; font-size: 11px; color: #8a8a8a; letter-spacing: 0.06em; }
#gl-auth-toggle a { color: #f4f4f4; cursor: pointer; text-decoration: underline; }
#gl-auth-toggle a:hover { color: #fff; }
#gl-auth-error { margin-top: 14px; min-height: 14px; text-align: center; font-size: 11px; color: var(--gl-error); letter-spacing: 0.04em; }

/* pixel "?" next to the Alpha Test Key label + its hover tooltip (Discord invite) */
#gl-alphakey-help {
  position: relative; display: inline-block; margin-left: 8px;
  color: #f4f4f4; font-size: 15px; font-weight: 700; line-height: 1;
  cursor: help; vertical-align: middle; user-select: none;
  text-shadow: 2px 2px 0 #000;
}
#gl-alphakey-help:hover { color: #fff; }
#gl-alphakey-tip {
  display: none; position: absolute; bottom: calc(100% + 10px); left: 50%; transform: translateX(-50%);
  width: 220px; padding: 10px 12px; z-index: 5;
  background: #050505; border: 2px solid #f4f4f4; box-shadow: 4px 4px 0 0 #000;
  color: #f4f4f4; font-size: 10px; font-weight: 400; line-height: 1.7; letter-spacing: 0.04em; text-transform: none;
  text-shadow: none;
}
/* invisible bridge across the 10px gap so the pointer can travel badge → tooltip without losing hover */
#gl-alphakey-tip::after {
  content: ""; position: absolute; top: 100%; left: 0; width: 100%; height: 14px;
}
#gl-alphakey-help:hover #gl-alphakey-tip { display: block; }
#gl-alphakey-tip a { color: #f4f4f4; text-decoration: underline; }
#gl-alphakey-tip a:hover { color: #fff; }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

/**
 * Show the login/register overlay. Resolves once the user is authenticated.
 * Phase 1 deliverable — replaced/augmented by in-game scenes later.
 */
export function showAuthOverlay(): Promise<void> {
  ensureStyle();
  showDiscordBadge();

  return new Promise<void>((resolve) => {
    let mode: "login" | "register" = "login";

    const overlay = document.createElement("div");
    overlay.id = "gl-auth-overlay";
    overlay.innerHTML = `
      <div id="gl-auth-card">
        <span class="gl-auth-corner tl"></span>
        <span class="gl-auth-corner tr"></span>
        <span class="gl-auth-corner bl"></span>
        <span class="gl-auth-corner br"></span>
        <p class="sub" id="gl-auth-mode-sub">Sign in to enter the world</p>
        <label for="gl-email">Email</label>
        <input id="gl-email" type="email" autocomplete="username" />
        <label for="gl-password">Password</label>
        <input id="gl-password" type="password" autocomplete="current-password" />
        <label for="gl-alphakey" id="gl-alphakey-label">Alpha Test Key<span id="gl-alphakey-help">?<span id="gl-alphakey-tip">Need a key? Reach out on our Discord and we'll set you up:<br><a href="https://discord.gg/xMAwUcDpXN" target="_blank" rel="noopener noreferrer">discord.gg/xMAwUcDpXN</a></span></span></label>
        <input id="gl-alphakey" type="text" autocomplete="off" spellcheck="false" placeholder="GHOST-XXXXX-XXXXX" />
        <button class="primary" id="gl-submit" type="button">Log in</button>
        <div id="gl-auth-error"></div>
        <div id="gl-auth-toggle">
          <span id="gl-toggle-text">New here?</span>
          <a id="gl-toggle-link">Create an account</a>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const logo = mountGhostLogo(overlay);

    const email = overlay.querySelector<HTMLInputElement>("#gl-email")!;
    const password = overlay.querySelector<HTMLInputElement>("#gl-password")!;
    const alphaKey = overlay.querySelector<HTMLInputElement>("#gl-alphakey")!;
    const alphaKeyLabel = overlay.querySelector<HTMLLabelElement>("#gl-alphakey-label")!;
    const submit = overlay.querySelector<HTMLButtonElement>("#gl-submit")!;
    const errorEl = overlay.querySelector<HTMLDivElement>("#gl-auth-error")!;
    const sub = overlay.querySelector<HTMLParagraphElement>("#gl-auth-mode-sub")!;
    const toggleText = overlay.querySelector<HTMLSpanElement>("#gl-toggle-text")!;
    const toggleLink = overlay.querySelector<HTMLAnchorElement>("#gl-toggle-link")!;

    function setMode(next: "login" | "register"): void {
      mode = next;
      errorEl.textContent = "";
      if (mode === "login") {
        sub.textContent = "Sign in to enter the world";
        submit.textContent = "Log in";
        toggleText.textContent = "New here?";
        toggleLink.textContent = "Create an account";
        password.autocomplete = "current-password";
        alphaKey.style.display = "none"; alphaKeyLabel.style.display = "none"; // invite key is register-only
      } else {
        sub.textContent = "Create an account to begin";
        submit.textContent = "Register";
        toggleText.textContent = "Have an account?";
        toggleLink.textContent = "Log in";
        password.autocomplete = "new-password";
        alphaKey.style.display = ""; alphaKeyLabel.style.display = "";
      }
    }

    function clientValidate(): string | null {
      if (!EMAIL_RE.test(email.value.trim())) return "Enter a valid email.";
      if (password.value.length < PASSWORD_MIN) return `Password must be at least ${PASSWORD_MIN} characters.`;
      if (mode === "register" && !alphaKey.value.trim()) return "Enter your Alpha Test Key.";
      return null;
    }

    async function onSubmit(): Promise<void> {
      const localError = clientValidate();
      if (localError) {
        errorEl.textContent = localError;
        return;
      }
      submit.disabled = true;
      errorEl.textContent = "";
      try {
        if (mode === "login") {
          await session.login(email.value.trim(), password.value);
        } else {
          await session.register(email.value.trim(), password.value, alphaKey.value.trim());
        }
        logo.dispose();
        overlay.remove();
        resolve();
      } catch (err) {
        errorEl.textContent = err instanceof Error ? err.message : "Something went wrong.";
        submit.disabled = false;
      }
    }

    submit.addEventListener("click", onSubmit);
    password.addEventListener("keydown", (e) => {
      if (e.key === "Enter") onSubmit();
    });
    alphaKey.addEventListener("keydown", (e) => {
      if (e.key === "Enter") onSubmit();
    });
    toggleLink.addEventListener("click", () => setMode(mode === "login" ? "register" : "login"));

    setMode("login");
    email.focus();
  });
}
